import { GET } from "lib/api";
import { observable } from "mobx";
import { debounce } from "lib/decorators";
import config from "lib/config";
import { sorters } from "./functions";
import {
  MODE_FULL,
  MODE_SIMPLE,
  NOOFCOURSES,
  PROGRESS_ALL,
  SORT_CREATED_DATE,
} from "./constants";

const portalSiteId = config.siteId;

const API_GetCourseStatus = "v1/user/:personalId/coursestatus/:courseId";
const API_GetTags = "v1/coursecataloguecategories/:portalSiteId/";
const API_Search = "/v1/user/:personalId/coursesforcardview";
const API_GetProgramsProgress = "v1/education/program/progress/:personalId";

export class MyCoursesCardStore {
  @observable isAdmin;
  @observable searching = false;
  @observable personalId;
  @observable _loadedCourses = {};
  @observable filteredCourses;
  @observable categories;
  @observable courses;
  @observable programs;
  @observable filteredPrograms;
  @observable tags;
  filter = null;
  originalCourses;

  async clear() {
    this.filter = {
      searchText: "",
      selectedTags: [0, 0, 0, 0, 0],
      sortBy: SORT_CREATED_DATE,
      mode: MODE_FULL,
    };
    this._loadedCourses = {};
    this.searching = false;
    this.categories = {};
    this.courses = [];
    this.programs = [];
    this.tags = [];
    this.filteredCourses = {};
    this.filteredPrograms = {};
    this.filter.filter = PROGRESS_ALL;
    this.noOfCourses = NOOFCOURSES;
  }

  async refresh(personalId) {
    this.clear();
    this.personalId = personalId;

    this.setMaximumNumberCoursesToBeDisplayed();

    await this._searchImmediate(this.filter);
    let trainingCategories = this.trainingCategories;
    this.categories = (trainingCategories || []).reduce((acc, tc) => {
      acc[tc.trainingCategoryId] = tc;
      return acc;
    }, {});

    this.categoryHierarchy = (trainingCategories || []).reduce((acc, tc) => {
      if (!acc[tc.parentId]) {
        acc[tc.parentId] = [];
      }
      acc[tc.parentId].push(tc);
      return acc;
    }, {});
    this.tags = await GET(API_GetTags, { portalSiteId });
    //det måste vara minst fem taggar för att anrop mot apiet ska gå igenom
    this.filter.selectedTags = [...this.tags.map(() => 0), 0, 0, 0, 0, 0].slice(
      0,
      Math.max(5, this.tags.length),
    );
  }

  async search(opts) {
    if (this.searching >= 2) return;

    this.searching = 1;
    this.courses = [];
    this.programs = [];
    this._updateFilteredCourses([]);
    this._updateFilteredPrograms([]);
    return this._searchEnqueue(opts);
  }

  @debounce(1000)
  async _searchEnqueue(opts) {
    return this._searchImmediate(opts);
  }

  async setMaximumNumberCoursesToBeDisplayed() {
    const amountCoursesToDisplay = await GET("v1/adminsettings/:settingName", {
      settingName: "noofcoursesinmycoursespanel",
    });

    this.noOfCourses = amountCoursesToDisplay;
  }

  async _searchImmediate(opts) {
    this.searching = 2;
    try {
      this.filter = opts;
      const categoryId = opts.categoryId || 0;
      const apiSearchUrlWithQueryParameters = `${API_Search}?${new URLSearchParams(
        {
          categoryId,
          searchPhrase: opts.searchText,
          sortBy: opts.sortBy,
          portalSiteId,
          profileId: 4,
          mode: MODE_FULL,
        },
      )}&${opts.selectedTags.map((tag) => "tags=" + tag).join("&")}`;

      const coursesInfo = await GET(apiSearchUrlWithQueryParameters, {
        personalId: this.personalId,
      });

      let { courses } = coursesInfo;
      this.trainingCategories = coursesInfo.trainingCategories;

      if (!opts.searchText) {
        this.originalCourses = courses;
        this.isAdmin = !!(this.originalCourses || []).find(
          (c) => c.profileId < 3,
        );
      }

      if (opts.hideAdministratorCourses) {
        courses = courses.filter(function (course) {
          return course.profileId > 2;
        });
      }

      if (opts.hideParticipantCourses) {
        courses = courses.filter(function (course) {
          return course.profileId < 3;
        });
      }

      this.serverCourses = courses;
      this.courses = courses;
      this._sortPrograms();
      this._getProgramsProgress();
      this._updateFilteredCourses(this.courses);
    } finally {
      this.searching = false;
    }
  }

  setSortBy(sortBy) {
    this.filter.sortBy = sortBy;
    this._updateCourseList();
    this._updateProgramList();
  }

  selectedFilterTags(selectedTags) {
    this.filter.selectedTags = selectedTags;
  }

  setFilter(filter) {
    if (filter && filter !== this.filter.filter) {
      this.filter.filter = filter;
      this._updateCourseList();
      this._updateProgramList();
    }
  }

  _updateList(type) {
    const sortBy = this.filter.sortBy || "";
    const sorter = sorters[sortBy.toLowerCase()] || sorters.title;
    const filter = this.filter.filter || "all";
    const filteredItems =
      this[`filtered${type}`][filter] || this[`filtered${type}`].all;
    this[`filtered${type}`].active = filteredItems ? sorter(filteredItems) : [];
  }

  _updateCourseList() {
    this._updateList("Courses");
  }

  _updateProgramList() {
    this._updateList("Programs");
  }

  _updateFilteredCourses(courses) {
    let filteredCourses = {};
    filteredCourses.active = courses;
    filteredCourses.all = courses;
    filteredCourses.notstarted = courses.filter(function (c) {
      return !c.lastLoggedIn && !c.completed;
    });
    filteredCourses.begun = courses.filter(function (c) {
      return c.lastLoggedIn && !c.completed;
    });
    filteredCourses.completed = courses.filter(function (c) {
      return c.completed;
    });
    filteredCourses.lefttodo = courses.filter(function (c) {
      return !c.completed;
    });
    this.filteredCourses = filteredCourses;
    this._updateCourseList();
  }

  _updateFilteredPrograms(programs) {
    let filteredPrograms = {};
    filteredPrograms.active = programs;
    filteredPrograms.all = programs;
    filteredPrograms.notstarted = programs.filter(
      (program) => program.progress === 0,
    );
    filteredPrograms.begun = programs.filter(
      (program) => !program.completed && program.progress > 0,
    );
    filteredPrograms.completed = programs.filter(
      (program) => program.completed === true,
    );
    filteredPrograms.lefttodo = programs.filter(
      (program) => !program.completed,
    );
    this.filteredPrograms = filteredPrograms;
    this._updateProgramList();
  }

  getCourseStatus(personalId, courseId) {
    const key = [personalId, courseId].join(":");
    if (this._loadedCourses[key]) return this._loadedCourses[key];

    return (this._loadedCourses[key] = GET(API_GetCourseStatus, {
      personalId,
      courseId,
    }));
  }

  _sortPrograms() {
    if (!this.courses) {
      this.programs = [];
      this.courses = [];
      return;
    }
    this.programs = this.courses.filter((course) => course.isProgram);
    this.courses = this.courses.filter((course) => !course.isProgram);
  }

  async _getProgramsProgress() {
    const programs = this.programs;
    const personalId = this.personalId;

    if (programs.length > 0) {
      const programProgress = await GET(API_GetProgramsProgress, {
        personalId,
      });

      programProgress.forEach((program) => {
        let programToSetProgress = programs.find(
          (course) =>
            course.courseId === program.programId &&
            course.activityId === program.activityId,
        );

        if (programToSetProgress) {
          programToSetProgress.progress = program.progress;
        }
      });

      this._updateFilteredPrograms(this.programs);
    }
  }
}

export default new MyCoursesCardStore();
